import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Mail, Search } from "lucide-react";

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In progress" },
  { key: "resolved", label: "Resolved" },
  { key: "closed", label: "Closed" },
];
const TOPIC_LABELS = { order: "Order", account: "Account", partnership: "Partnership", press: "Press", other: "Other" };
const NEXT_STATUS = { open: "in_progress", in_progress: "resolved", resolved: "closed" };

export default function AdminSupportTab() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const params = {};
      if (status) params.status = status;
      const { data } = await api.get("/support/admin", { params });
      setData(data);
    } catch (e) { setError(e.response?.data?.detail || "Load error"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const advance = async (id, current) => {
    const next = NEXT_STATUS[current]; if (!next) return;
    setBusy(true);
    try { await api.post(`/support/admin/${id}/status`, { status: next }); await load(); }
    catch (e) { setError(e.response?.data?.detail || "Update failed"); } finally { setBusy(false); }
  };

  const tickets = (data?.tickets || []).filter(t => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return t.email?.toLowerCase().includes(needle) || t.name?.toLowerCase().includes(needle) || t.message?.toLowerCase().includes(needle);
  });

  return (
    <div className="space-y-4" data-testid="admin-support-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl">Support inbox</h2>
          <p className="text-sm text-[var(--ff-text-muted)] mt-1">{data ? `${tickets.length} of ${data.count} tickets` : "Loading…"}</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ff-text-muted)]"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search email, name, message" className="input-base pl-9 text-sm w-72" data-testid="support-search"/>
        </div>
      </div>

      {error && <div className="bg-rose-50 border border-rose-200 p-3 rounded text-sm text-rose-800" data-testid="support-error">{error}</div>}

      <div className="flex gap-2 flex-wrap" data-testid="support-status-filters">
        {STATUS_FILTERS.map(f => (
          <button key={f.key || "all"} onClick={() => setStatus(f.key)} data-testid={`support-status-${f.key || "all"}`}
            className={`px-3 py-1.5 rounded-full border text-xs font-medium ${status === f.key ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "bg-white border-[var(--ff-border)] text-[var(--ff-text-secondary)] hover:border-[var(--ff-primary)]"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="card-flat divide-y divide-[var(--ff-border)]" data-testid="support-list">
        {!data && <p className="p-8 text-sm text-[var(--ff-text-muted)]">Loading…</p>}
        {data && tickets.length === 0 && <p className="p-8 text-sm text-[var(--ff-text-muted)]">No tickets match.</p>}
        {tickets.map(t => {
          const next = NEXT_STATUS[t.status];
          return (
            <div key={t.id} className="p-5" data-testid={`ticket-${t.id}`}>
              <div className="flex items-start gap-4 flex-wrap">
                <Mail size={16} className="text-[var(--ff-text-muted)] mt-1 shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-mono text-xs text-[var(--ff-text-secondary)]">{t.id}</p>
                    <span className="text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded-full border bg-stone-50 text-stone-700 border-stone-200">{TOPIC_LABELS[t.topic] || t.topic}</span>
                    <span className="text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200">{t.status}</span>
                    {t.order_id && <span className="text-[10px] text-[var(--ff-text-muted)]">order {t.order_id}</span>}
                  </div>
                  <p className="font-medium text-sm mt-2">{t.name} <span className="text-[var(--ff-text-muted)] font-normal">&lt;{t.email}&gt;</span></p>
                  <p className="text-sm text-[var(--ff-text-secondary)] mt-2 whitespace-pre-wrap">{t.message}</p>
                  <p className="text-xs text-[var(--ff-text-muted)] mt-2">Received {new Date(t.created_at).toLocaleString()}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <a href={`mailto:${t.email}?subject=Re: [${t.id}] your support ticket`} className="btn-secondary text-sm" data-testid={`reply-${t.id}`}>Reply</a>
                  {next && (
                    <button disabled={busy} onClick={() => advance(t.id, t.status)} className="btn-primary text-sm" data-testid={`advance-${t.id}`}>
                      Move to {next.replace("_", " ")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
